import { Grid, Stack, Switch, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

import { Declarant, DeclarationHighValueGroupType, EnvironmentCountries } from '@e-origin/shared';

import { ContainerList, EucdmCode, HideElementFor, MultiLangCodes, MultipleContainers, TextInput } from '@components';
import { retrieveCodes } from '@hooks/retrieveCodes';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { isHighValueDeclaration } from '@utils';
import { DeclarationGeneralFormValues } from './validateDeclarationGeneralForm';

const CONSIGNMENT_COUNTRY_OF_DISPATCH = 'consignment.countryOfDispatch';
const CONSIGNMENT_COUNTRY_OF_EXPORT = 'consignment.countryOfExport';
const CONSIGNMENT_COUNTRY_OF_DESTINATION = 'consignment.countryOfDestination';
const TRANSPORT_ACTIVE_BORDER_NATIONALITY = 'transportInformation.activeBorderTransportMeans.nationality';
const TRANSPORT_MODE_AT_BORDER = 'transportInformation.modeOfTransportMeansAtTheBorder';
const TRANSPORT_DEPARTURE_TYPE = 'transportInformation.departureTransportMeans.typeOfIdentification';
const TRANSPORT_DEPARTURE_NATIONALITY = 'transportInformation.departureTransportMeans.nationality';
const TRANSPORT_ACTIVE_BORDER_TYPE = 'transportInformation.activeBorderTransportMeans.typeOfIdentification';
const TRANSPORT_INLAND_MODE = 'transportInformation.inlandModeOfTransport';
const PACKAGING_TYPE = 'declarationGoods.packaging.typeOfPackages';
const TRANSPORT_ARRIVAL_TYPE = 'transportInformation.arrivalTransportMeans.typeOfIdentification';

interface TransportAndPackagingSectionProps {
  form: UseFormReturnType<DeclarationGeneralFormValues>;
  editDisabled: boolean;
  declarant: Declarant;
  declarationGroup: string;
  isExport: boolean;
}

export const TransportAndPackagingSection: React.FC<TransportAndPackagingSectionProps> = ({
  form,
  declarant,
  declarationGroup,
  isExport = false,
  editDisabled = false,
}) => {
  const { data: settings } = useSettings();
  const codelistSystems = useCodeListSystem({
    group: declarationGroup,
    isExport,
  });

  const isNlExport = settings.country === EnvironmentCountries.NL && isExport;

  const SwitchRightSection = ({ field, disabled }: { field: string; disabled?: boolean }) => (
    <Switch {...form.getInputProps(field, { type: 'checkbox' })} disabled={editDisabled || disabled} size="xs" mr="sm" />
  );

  return (
    <Stack gap="xl" mb="xl">
      <Stack gap="md">
        <Title order={5} fw={600}>
          Transport
        </Title>

        <Grid>
          {!isExport && (
            <Grid.Col span={6}>
              <EucdmCode path={CONSIGNMENT_COUNTRY_OF_DISPATCH}>
                <MultiLangCodes
                  {...form.getInputProps(CONSIGNMENT_COUNTRY_OF_DISPATCH)}
                  label="Country of Dispatch"
                  code="CL199"
                  onChange={(selectedOption) => form.setFieldValue(CONSIGNMENT_COUNTRY_OF_DISPATCH, selectedOption)}
                  disabled={editDisabled}
                  rightSection={<SwitchRightSection field="consignment.countryOfDispatchHeader" />}
                  system={codelistSystems}
                  data-pw="country-dispatch-input"
                />
              </EucdmCode>
            </Grid.Col>
          )}

          {isExport && (
            <Grid.Col span={6}>
              <MultiLangCodes
                {...form.getInputProps(CONSIGNMENT_COUNTRY_OF_EXPORT)}
                label="Country of Export"
                code={settings.country === EnvironmentCountries.NL ? 'CL010' : 'CL008'}
                onChange={(selectedOption) => form.setFieldValue(CONSIGNMENT_COUNTRY_OF_EXPORT, selectedOption)}
                disabled={editDisabled || !form.values.consignment.countryOfExportHeader}
                rightSection={<SwitchRightSection field="consignment.countryOfExportHeader" disabled={!form.values.consignment.countryOfExportHeader} />}
                system={codelistSystems}
                data-pw="country-export-input"
              />
            </Grid.Col>
          )}
        </Grid>

        <Grid>
          <Grid.Col span={6}>
            <EucdmCode path={CONSIGNMENT_COUNTRY_OF_DESTINATION}>
              <MultiLangCodes
                {...form.getInputProps(CONSIGNMENT_COUNTRY_OF_DESTINATION)}
                label="Country of Destination"
                code={isExport ? 'CL008' : 'CL010'}
                onChange={(selectedOption) => form.setFieldValue(CONSIGNMENT_COUNTRY_OF_DESTINATION, selectedOption)}
                disabled={editDisabled}
                rightSection={<SwitchRightSection field="consignment.destinationHeader" />}
                system={codelistSystems}
                data-pw="country-destination-input"
              />
            </EucdmCode>
          </Grid.Col>
          <HideElementFor systems={['DMS']} declarationGroup={declarationGroup} condition={() => isExport}>
            <Grid.Col span={6}>
              <EucdmCode path={'consignment.regionOfDestination'}>
                <TextInput
                  {...form.getInputProps('consignment.regionOfDestination')}
                  label="Region of Destination"
                  disabled={editDisabled}
                  rightSection={<SwitchRightSection field="consignment.destinationHeader" />}
                  data-pw="region-destination-input"
                />
              </EucdmCode>
            </Grid.Col>
          </HideElementFor>
        </Grid>

        {isExport && (
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                {...form.getInputProps('consignment.carrier')}
                label="Carrier EORI"
                disabled={editDisabled}
                rightSection={<SwitchRightSection field="consignment.carrierHeader" />}
                data-pw="carrier-input"
              />
            </Grid.Col>
          </Grid>
        )}

        {isExport && (
          <Grid>
            <Grid.Col span={4}>
              <TextInput
                {...form.getInputProps('transportInformation.departureTransportMeans.identificationNumber')}
                label="Departure transport means identification"
                disabled={editDisabled}
                data-pw="departure-id-input"
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <MultiLangCodes
                {...form.getInputProps(TRANSPORT_DEPARTURE_TYPE)}
                label="Departure transport means type"
                code="CL750"
                onChange={(selectedOption) => form.setFieldValue(TRANSPORT_DEPARTURE_TYPE, selectedOption)}
                disabled={editDisabled}
                system={codelistSystems}
                data-pw="departure-type-input"
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <MultiLangCodes
                {...form.getInputProps(TRANSPORT_DEPARTURE_NATIONALITY)}
                label="Departure transport means nationality"
                code="CL165"
                onChange={(selectedOption) => form.setFieldValue(TRANSPORT_DEPARTURE_NATIONALITY, selectedOption)}
                disabled={editDisabled}
                system={codelistSystems}
                data-pw="departure-nationality-input"
              />
            </Grid.Col>
          </Grid>
        )}

        <Grid>
          {isExport && (
            <>
              <Grid.Col span={3}>
                <TextInput
                  {...form.getInputProps('transportInformation.activeBorderTransportMeans.identificationNumber')}
                  label="Active border transport means ID"
                  disabled={editDisabled}
                  data-pw="active-border-id-input"
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <MultiLangCodes
                  {...form.getInputProps(TRANSPORT_ACTIVE_BORDER_TYPE)}
                  label={isExport ? 'Active border transport means ID type' : 'Active border transport means type'}
                  code="TypeOfIdentificationofMeansOfTr"
                  onChange={(selectedOption) => form.setFieldValue(TRANSPORT_ACTIVE_BORDER_TYPE, selectedOption)}
                  disabled={editDisabled}
                  system={codelistSystems}
                  data-pw="active-border-type-input"
                />
              </Grid.Col>
            </>
          )}
          <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2}>
            <Grid.Col span={isExport ? 3 : 6}>
              <EucdmCode path={TRANSPORT_ACTIVE_BORDER_NATIONALITY}>
                <MultiLangCodes
                  {...form.getInputProps(TRANSPORT_ACTIVE_BORDER_NATIONALITY)}
                  label="Active border transport means nationality"
                  code="CL165"
                  onChange={(selectedOption) => form.setFieldValue(TRANSPORT_ACTIVE_BORDER_NATIONALITY, selectedOption)}
                  disabled={editDisabled}
                  system={codelistSystems}
                  data-pw="active-border-nationality-input"
                />
              </EucdmCode>
            </Grid.Col>
          </HideElementFor>
          <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2 && settings.country === EnvironmentCountries.BE}>
            <Grid.Col span={isExport ? 3 : 6}>
              <EucdmCode path={TRANSPORT_MODE_AT_BORDER}>
                <MultiLangCodes
                  {...form.getInputProps(TRANSPORT_MODE_AT_BORDER)}
                  label="Transport mode at border"
                  code="CL018"
                  onChange={(selectedOption) => form.setFieldValue(TRANSPORT_MODE_AT_BORDER, selectedOption)}
                  disabled={editDisabled}
                  system={codelistSystems}
                  data-pw="border-transport-mode-input"
                />
              </EucdmCode>
            </Grid.Col>
          </HideElementFor>
        </Grid>

        <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2}>
          <Grid>
            {!isExport && (
              <>
                <Grid.Col span={4}>
                  <EucdmCode path={'transportInformation.arrivalTransportMeans.identificationNumber'}>
                    <TextInput
                      {...form.getInputProps('transportInformation.arrivalTransportMeans.identificationNumber')}
                      label="Arrival transport means ID"
                      disabled={editDisabled}
                      data-pw="arrival-id-input"
                    />
                  </EucdmCode>
                </Grid.Col>
                <Grid.Col span={4}>
                  <EucdmCode path={TRANSPORT_ARRIVAL_TYPE}>
                    <MultiLangCodes
                      {...form.getInputProps(TRANSPORT_ARRIVAL_TYPE)}
                      label="Arrival Transport means type"
                      code="CL750"
                      onChange={(selectedOption) => form.setFieldValue(TRANSPORT_ARRIVAL_TYPE, Number(selectedOption) || undefined)}
                      disabled={editDisabled}
                      system={codelistSystems}
                      data-pw="arrival-type-input"
                    />
                  </EucdmCode>
                </Grid.Col>
              </>
            )}
            <Grid.Col span={4}>
              <EucdmCode path={TRANSPORT_INLAND_MODE}>
                <MultiLangCodes
                  {...form.getInputProps(TRANSPORT_INLAND_MODE)}
                  label={(() => {
                    if (isNlExport) {
                      return 'Departure transport means mode';
                    }

                    return settings.country === EnvironmentCountries.NL && isHighValueDeclaration(declarationGroup) && !isExport
                      ? 'Arrival transport means mode'
                      : 'Inland transport mode';
                  })()}
                  code="CL018"
                  onChange={(selectedOption) => form.setFieldValue(TRANSPORT_INLAND_MODE, selectedOption)}
                  disabled={editDisabled}
                  system={codelistSystems}
                  data-pw="inland-transport-mode-input"
                />
              </EucdmCode>
            </Grid.Col>
          </Grid>
        </HideElementFor>

        <Grid>
          <Grid.Col span={6}>
            <ContainerList
              containers={form.values.transportInformation.transportEquipment.containers}
              onChange={(newContainers) => form.setFieldValue('transportInformation.transportEquipment.containers', newContainers)}
              disabled={editDisabled}
              isExport={isExport}
              rightSection={
                <Switch
                  {...form.getInputProps('transportInformation.transportEquipment.header')}
                  checked={form.values.transportInformation.transportEquipment.header}
                  size="xs"
                  disabled={editDisabled}
                />
              }
              data-pw="containers-input"
            />
          </Grid.Col>
        </Grid>

        {isExport && (
          <Grid>
            <Grid.Col span={12}>
              <MultipleContainers
                containers={form.values.transportInformation.countryOfRoutingOfConsignment.map((c) => c.country)}
                onChange={(newList) =>
                  form.setFieldValue(
                    'transportInformation.countryOfRoutingOfConsignment',
                    newList.map((c) => ({ country: c })),
                  )
                }
                editDisabled={editDisabled}
                label="Routing of Consignment country"
                fetchOptions={(search) => retrieveCodes('CL008', search, declarant?.language, codelistSystems)}
                data-pw="routing-countries-input"
              />
            </Grid.Col>
          </Grid>
        )}
      </Stack>

      <Stack gap="md">
        <Title order={5} fw={600}>
          Packaging
        </Title>

        <Grid>
          <Grid.Col span={6}>
            <EucdmCode path="goodsShipmentItems.packaging.typeOfPackages">
              <MultiLangCodes
                {...form.getInputProps(PACKAGING_TYPE)}
                label="Package Type"
                code="CL017"
                onChange={(selectedOption) => form.setFieldValue(PACKAGING_TYPE, selectedOption)}
                disabled={editDisabled}
                system={codelistSystems}
                data-pw="package-type-input"
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>
      </Stack>
    </Stack>
  );
};
